import { Component } from "react";
import PropTypes from "prop-types";
import { AlertTriangle, RefreshCw, Home, LogIn, Mail } from "lucide-react";
import Button from "@shared/components/base/Button";

/* global process */

class AuthErrorBoundary extends Component {
  static propTypes = {
    children: PropTypes.node.isRequired,
    fallbackMessage: PropTypes.string,
    showContactSupport: PropTypes.bool,
    onRetry: PropTypes.func,
  };

  static defaultProps = {
    fallbackMessage:
      "Something went wrong with authentication. Please try again.",
    showContactSupport: true,
    onRetry: null,
  };

  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });

    // Log to monitoring service
    console.error("Authentication Error:", error, errorInfo);

    // Optional: Send to error reporting service
    // if (window.Sentry) {
    //   window.Sentry.captureException(error, { contexts: { errorInfo } });
    // }
  }

  handleRetry = () => {
    if (this.props.onRetry) {
      this.props.onRetry();
    } else {
      window.location.reload();
    }
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  handleGoToLogin = () => {
    window.location.href = "/admin/login";
  };

  handleContactSupport = () => {
    window.location.href = "/contact";
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 text-center">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Authentication Error
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              {this.props.fallbackMessage}
            </p>

            <div className="space-y-3">
              <Button
                onClick={this.handleRetry}
                className="w-full"
                variant="primary"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>

              <Button
                onClick={this.handleGoToLogin}
                className="w-full"
                variant="secondary"
              >
                <LogIn className="h-4 w-4 mr-2" />
                Back to Login
              </Button>

              <Button
                onClick={this.handleGoHome}
                className="w-full"
                variant="secondary"
              >
                <Home className="h-4 w-4 mr-2" />
                Go to Homepage
              </Button>

              {this.props.showContactSupport && (
                <Button
                  onClick={this.handleContactSupport}
                  className="w-full"
                  variant="outline"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Contact Support
                </Button>
              )}
            </div>

            {/* Development mode error details */}
            {typeof process !== "undefined" &&
              process.env &&
              process.env.NODE_ENV === "development" &&
              this.state.error && (
                <details className="mt-6 text-left">
                  <summary className="text-sm text-gray-500 cursor-pointer">
                    Error Details (Development)
                  </summary>
                  <pre className="mt-2 text-xs bg-gray-100 dark:bg-gray-700 p-2 rounded overflow-auto">
                    {this.state.error.toString()}
                    {this.state.errorInfo?.componentStack}
                  </pre>
                </details>
              )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default AuthErrorBoundary;
